<script setup lang="ts">
import { type AdvertisersCategories, ChipComponent } from '@metapic-apps/shared'
import { ref, toRef } from 'vue'

import { useGetAdvertiserListRequest, useGetAdvertisersCategoriesListRequest } from '@/api'
import CheckIcon from '@/assets/icons/check.svg?component'
import ChevronIcon from '@/assets/icons/chevron.svg?component'
import { capitalizeFirstLetter } from '@/lib/helpers'
import type { AdvertiserListItemType } from '@/lib/types/advertiser.types'
import type { ReturnAdFormType } from '@/lib/types/return-ad.types'
import { BoxComponent, FlexComponent, InputComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  advertiserGroup: number
  isEditMode: boolean
  name?: string
}>()

const advertiserList = defineModel<ReturnAdFormType['advertiserList']>('advertiserList', { required: true })
const categories = defineModel<ReturnAdFormType['categories']>('categories', { required: true })
const isAllAdvertisersSelected = defineModel<ReturnAdFormType['isAllAdvertisersSelected']>('isAllAdvertisersSelected', { required: true })
const advertiserGroup = toRef(() => props.advertiserGroup)
const advertisersSearchQuery = ref('')

const {
  data: advertiserOptions,
  isLoading: isAdvertisersLoading,
  totalAmount: advertisersTotalAmount,
} = useGetAdvertiserListRequest(advertiserGroup, advertisersSearchQuery)
const { data: categoriesOptions, isLoading: isCategoriesLoading } = useGetAdvertisersCategoriesListRequest()

const toggleCategory = (category: AdvertisersCategories) => {
  if (categories.value.filter((item) => item.id === category.id).length) {
    categories.value = categories.value.filter((item) => item.id !== category.id)
  } else {
    categories.value.push(category)
  }
}

const toggleAdvertiser = (advertiser: AdvertiserListItemType) => {
  if (isAllAdvertisersSelected.value) {
    isAllAdvertisersSelected.value = false
  }

  if (advertiserList.value.filter((item) => item.id === advertiser.id).length) {
    advertiserList.value = advertiserList.value.filter((item) => item.id !== advertiser.id)
  } else {
    advertiserList.value.push(advertiser)
  }
}

const toggleSelectAll = () => {
  if (isAllAdvertisersSelected.value) {
    isAllAdvertisersSelected.value = false
  } else {
    advertiserList.value = []
    categories.value = []
    isAllAdvertisersSelected.value = true
  }
}
</script>

<template>
  <el-dropdown trigger="click" class="select" data-test="return-ad-advertiser-selector" :name="name">
    <BoxComponent :p="8" :min-w="140">
      <FlexComponent :gap="8" align="center" justify="space-between">
        <TypographyComponent v-if="isAllAdvertisersSelected" variant="body-large">All {{ advertisersTotalAmount }}</TypographyComponent>
        <FlexComponent v-else-if="categories.length || advertiserList.length" :gap="8" wrap="wrap">
          <ChipComponent v-for="category in categories" :key="category.id" closable @close="() => toggleCategory(category)">
            {{ capitalizeFirstLetter(category.name) }}
          </ChipComponent>
          <template v-if="advertiserList.length">
            <ChipComponent
              v-for="advertiser in advertiserList.slice(0, 4 - categories.length)"
              :key="advertiser.id"
              closable
              @close="() => toggleAdvertiser(advertiser)"
            >
              {{ advertiser.label }}
            </ChipComponent>
          </template>
          <ChipComponent v-if="categories.length + advertiserList.length > 4">
            + {{ categories.length + advertiserList.length - 4 }}
          </ChipComponent>
        </FlexComponent>
        <LoadingComponent v-else-if="(isAdvertisersLoading || isCategoriesLoading) && !categories.length && !advertiserList.length" />
        <TypographyComponent v-else variant="body-large" color="var(--secondary-18)"> Select advertiser </TypographyComponent>

        <el-icon :size="24" class="chevron-icon">
          <ChevronIcon />
        </el-icon>
      </FlexComponent>
    </BoxComponent>
    <template #dropdown>
      <div class="select__scrollable-content" data-test="return-ad-advertiser-selector__dropdown-content">
        <BoxComponent :p="16">
          <InputComponent v-model="advertisersSearchQuery" size="small" placeholder="Search advertisers" />
        </BoxComponent>
        <template v-if="!advertisersSearchQuery">
          <BoxComponent :pb="8" :pl="16">
            <TypographyComponent variant="title-large">Categories</TypographyComponent>
          </BoxComponent>
          <div
            v-for="option in categoriesOptions"
            :key="option.id"
            :class="['select__item', { 'is-disabled': isAllAdvertisersSelected }]"
            data-test="return-ad-advertiser-selector__category-item"
            @click="
              () => {
                if (!isAllAdvertisersSelected) toggleCategory(option)
              }
            "
          >
            <FlexComponent justify="space-between" align="center" :gap="8">
              <TypographyComponent variant="body-large" :color="isAllAdvertisersSelected ? 'var(--secondary-18)' : 'var(--primary)'">
                {{ option.name }}
              </TypographyComponent>
              <el-icon v-if="categories.filter((item) => item.id === option.id).length || isAllAdvertisersSelected" :size="16">
                <CheckIcon />
              </el-icon>
            </FlexComponent>
          </div>
        </template>

        <div class="select__divider" />

        <BoxComponent :pb="8" :pl="16" :pt="16">
          <TypographyComponent variant="title-large">Advertisers</TypographyComponent>
        </BoxComponent>

        <BoxComponent v-if="isAdvertisersLoading" :mt="24"><LoadingComponent /></BoxComponent>
        <template v-else>
          <template v-if="advertiserOptions.length">
            <div
              v-for="option in advertiserOptions"
              :key="option.id"
              :class="['select__item', { 'is-disabled': isAllAdvertisersSelected }]"
              data-test="return-ad-advertiser-selector__advertiser-item"
              @click="
                () => {
                  if (!isAllAdvertisersSelected) toggleAdvertiser(option)
                }
              "
            >
              <FlexComponent justify="space-between" align="center" :gap="8">
                <TypographyComponent variant="body-large" :color="isAllAdvertisersSelected ? 'var(--secondary-18)' : 'var(--primary)'">
                  {{ option.label }}
                </TypographyComponent>
                <el-icon v-if="advertiserList.filter((item) => item.id === option.id).length || isAllAdvertisersSelected" :size="16">
                  <CheckIcon />
                </el-icon>
              </FlexComponent>
            </div>
          </template>
          <BoxComponent v-else :pl="16" :mt="16">
            <TypographyComponent variant="body-medium" color="var(--secondary-48)">No results found</TypographyComponent>
          </BoxComponent>
        </template>
      </div>
      <div class="select__divider" />
      <BoxComponent
        :pt="24"
        :pl="16"
        :pr="16"
        :pb="16"
        class="select__all-btn"
        data-test="return-ad-advertiser-selector__select-all"
        @click="toggleSelectAll"
      >
        <FlexComponent justify="space-between" align="center" :gap="8">
          <TypographyComponent variant="body-large"> {{ isAllAdvertisersSelected ? 'Unselect' : 'Select' }} all </TypographyComponent>
          <el-icon v-if="isAllAdvertisersSelected" :size="16">
            <CheckIcon />
          </el-icon>
        </FlexComponent>
      </BoxComponent>
    </template>
  </el-dropdown>
</template>

<style scoped lang="scss">
.select {
  width: 100%;
}

:deep(.el-tooltip__trigger) {
  width: 100%;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--secondary-12);

  cursor: pointer;
  &:hover,
  &[aria-expanded='true'] {
    border-radius: 8px;
  }
  i {
    transition: all 0.3s ease-in;
  }

  &[aria-expanded='true'] {
    .chevron-icon {
      transform: rotate(180deg);
    }
  }
}

.select__scrollable-content {
  height: 364px;
  width: 590px;
  overflow: auto;
}

.select__item {
  padding: 16px;
  cursor: pointer;

  &.is-disabled {
    cursor: default;
  }

  &:not(.is-disabled):hover {
    background-color: var(--secondary-8);
  }
}

.select__divider {
  height: 1px;
  background-color: var(--secondary-12);
}

.select__all-btn {
  cursor: pointer;
}
</style>
